import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const HERO_IMAGES = [
  "https://readdy.ai/api/search-image?query=aerial%20view%20of%20Tokyo%20city%20skyline%20at%20dusk%20with%20glowing%20skyscrapers%2C%20Tokyo%20Bay%20waterfront%2C%20modern%20urban%20landscape%2C%20dramatic%20golden%20hour%20light%2C%20cinematic%20wide%20angle%20photography%2C%20deep%20navy%20blue%20sky%20with%20warm%20city%20lights%2C%20ultra%20high%20resolution%2C%20professional%20architectural%20photography&width=1920&height=1080&seq=hero001&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Japanese%20business%20professionals%20in%20modern%20glass%20office%20building%2C%20handshake%20partnership%20meeting%2C%20city%20skyline%20visible%20through%20floor%20to%20ceiling%20windows%2C%20elegant%20corporate%20atmosphere%2C%20warm%20professional%20lighting%2C%20sophisticated%20interior%20design%2C%20high%20resolution%20business%20photography&width=1920&height=1080&seq=hero002&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Japan%20Mount%20Fuji%20at%20sunrise%20with%20dramatic%20orange%20sky%2C%20misty%20morning%20landscape%2C%20serene%20and%20majestic%20natural%20scenery%2C%20wide%20angle%20cinematic%20photography%2C%20deep%20atmospheric%20colors%2C%20ultra%20high%20resolution%20landscape%20photography%2C%20peaceful%20and%20powerful&width=1920&height=1080&seq=hero003&orientation=landscape",
  "https://readdy.ai/api/search-image?query=modern%20Japanese%20city%20street%20at%20night%20with%20neon%20lights%20and%20busy%20pedestrians%2C%20Shibuya%20crossing%20style%2C%20vibrant%20urban%20energy%2C%20long%20exposure%20photography%2C%20colorful%20light%20trails%2C%20dynamic%20city%20life%2C%20cinematic%20wide%20angle%20shot&width=1920&height=1080&seq=hero004&orientation=landscape",
];

export default function HeroSection() {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(30px)";
    const timer = setTimeout(() => {
      el.style.transition = "opacity 1s ease, transform 1s ease";
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const next = (currentIndex + 1) % HERO_IMAGES.length;
      setNextIndex(next);
      setTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(next);
        setTransitioning(false);
      }, 1200);
    }, 5000);
    return () => clearInterval(interval);
  }, [currentIndex]);

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const scrollDown = () => {
    const el = document.getElementById("about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative w-full h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Background Images */}
      <div className="absolute inset-0">
        {/* Current image */}
        <img
          src={HERO_IMAGES[currentIndex]}
          alt="Hero background"
          className="absolute inset-0 w-full h-full object-cover object-top"
          style={{ opacity: transitioning ? 0 : 1, transition: "opacity 1.2s ease" }}
        />
        {/* Next image (fades in) */}
        <img
          src={HERO_IMAGES[nextIndex]}
          alt="Hero background next"
          className="absolute inset-0 w-full h-full object-cover object-top"
          style={{ opacity: transitioning ? 1 : 0, transition: "opacity 1.2s ease" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a2332]/70 via-[#1a2332]/50 to-[#1a2332]/80"></div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {HERO_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => { setNextIndex(i); setTransitioning(true); setTimeout(() => { setCurrentIndex(i); setTransitioning(false); }, 1200); }}
            className={`h-0.5 transition-all duration-500 cursor-pointer ${i === currentIndex ? "w-8 bg-white" : "w-4 bg-white/40"}`}
          />
        ))}
      </div>

      {/* Content */}
      <div ref={titleRef} className="relative z-10 w-full text-center px-6 md:px-10 max-w-5xl mx-auto">
        {/* Tagline */}
        <p className="text-white/60 text-xs md:text-sm tracking-[0.4em] font-light mb-6 uppercase">
          {t("hero.tagline")}
        </p>

        {/* Main Title */}
        <h1 className="text-white font-black text-4xl md:text-6xl lg:text-7xl leading-tight mb-3">
          {t("hero.title1")}
        </h1>
        <h2 className="text-white font-black text-4xl md:text-6xl lg:text-7xl leading-tight mb-6">
          {t("hero.title2")}
        </h2>

        {/* Subtitle */}
        <p className="text-white/70 text-sm md:text-base font-light tracking-widest mb-10">
          {t("hero.subtitle")}
        </p>

        {/* Description */}
        <p className="text-white/80 text-base md:text-lg font-light max-w-2xl mx-auto mb-12 leading-relaxed">
          {t("hero.description")}
        </p>

        {/* CTA Button */}
        <div className="flex flex-col items-center justify-center">
          <button
            onClick={scrollToContact}
            className="bg-white text-[#1a2332] font-semibold text-sm px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 cursor-pointer whitespace-nowrap tracking-wider min-w-[220px]"
          >
            {t("hero.cta")}
          </button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollDown}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 hover:text-white/80 transition-colors cursor-pointer z-20"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">{t("hero.scroll")}</span>
        <div className="w-px h-10 bg-white/30 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-white/70 animate-bounce"></div>
        </div>
      </button>
    </section>
  );
}
